import React, { useCallback, useState } from 'react';

import styled from '@tanium/react-emotion-9';
import { useResizeObserver } from '@tanium/react-use-resize-observer';

import { Svg } from '../Svg';

const SvgWrapper = styled('div', {
  shouldForwardProp: (p) => !['animation', 'innerRef', 'overflow', 'transformOrigin', 'transform'].includes(p),
})<{
  animation: string;
  overflow?: string;
  transformOrigin?: string;
  transform?: string;
}>(({ animation, overflow, transform, transformOrigin }) => ({
  position: 'absolute',
  overflow,
  transform,
  transformOrigin,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  '> *': {
    pointerEvents: 'auto',
  },
  animation,
}));

interface AnimationSvgWrapperProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'css'> {
  animation: string;
  children: React.ReactNode | ((args: { width: number; height: number }) => React.ReactNode);
  overflow?: string;
  renderFn?: (args: { width: number; height: number }) => JSX.Element;
  transform?: string;
  transformOrigin?: string;
}

const defaultRenderFn = (args: { children: React.ReactNode; width: number; height: number }) => <Svg {...args} />;

export const AnimationSvgWrapper = React.memo(({ children, ...props }: AnimationSvgWrapperProps) => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>();
  const ref = useResizeObserver(
    useCallback(({ contentRect: { height, width } }) => {
      setDimensions({ height, width });
    }, []),
  );

  const getInnerContent = () => {
    if (!dimensions) {
      return null;
    }

    if (typeof children === 'function') {
      return children({ ...dimensions }) as React.ReactNode;
    }

    return defaultRenderFn({ ...dimensions, children });
  };

  return (
    <SvgWrapper {...props} innerRef={ref}>
      {getInnerContent()}
    </SvgWrapper>
  );
});
